<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header -->
      <div class="hidden md:flex items-center justify-between mb-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Payments</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block w-28 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ meta.total }} total payment{{ meta.total !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          <span class="hidden sm:inline">Add Payment</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- Filters row -->
      <div class="hidden md:flex flex-wrap items-center gap-2 mb-4">
        <!-- Customer filter -->
        <div class="relative" ref="filterCustomerDropdownRef">
          <button type="button" @click="toggleFilterCustomerDropdown"
            class="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer min-w-[140px]">
            <span class="flex-1 text-left truncate">{{ filterCustomerLabel }}</span>
            <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="filterCustomerDropdownOpen"
            class="absolute z-50 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                   rounded-xl shadow-lg overflow-hidden">
            <div class="p-2 border-b border-slate-100 dark:border-slate-700">
              <input v-model="filterCustomerSearch" type="text" placeholder="Search by name or shop..."
                class="w-full bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400
                       rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                @click.stop
                @keydown.down.prevent="moveFilterActive(1)"
                @keydown.up.prevent="moveFilterActive(-1)"
                @keydown.enter.prevent="confirmFilterActive"
                @keydown.escape="filterCustomerDropdownOpen = false" />
            </div>
            <div class="max-h-48 overflow-y-auto" ref="filterCustomerListRef">
              <button type="button" @click="selectFilterCustomer('')"
                class="w-full text-left px-3 py-2.5 text-sm transition"
                :class="!f.customer_id
                  ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                All Customers
              </button>
              <button v-for="(c, idx) in filteredFilterCustomers" :key="c.id" type="button"
                :ref="el => { if (el) filterCustomerItemRefs[idx] = el }"
                @click="selectFilterCustomer(String(c.id))"
                class="w-full text-left px-3 py-2.5 text-sm transition"
                :class="idx === activeFilterCustomerIndex
                  ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                  : String(c.id) === f.customer_id
                    ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                <p>{{ c.name }}</p>
                <p class="text-xs text-slate-400">{{ c.shop_name }}</p>
              </button>
              <p v-if="filteredFilterCustomers.length === 0" class="px-3 py-4 text-sm text-slate-400 text-center">No customers found</p>
            </div>
          </div>
        </div>

        <!-- Date preset filter -->
        <div class="relative">
          <select v-model="f.date_preset" @change="onDatePresetChange"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom...</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Custom date range -->
        <template v-if="f.date_preset === 'custom'">
          <input v-model="f.date_from" type="date" @change="applyFilters"
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
          <span class="self-center text-slate-400 text-sm">to</span>
          <input v-model="f.date_to" type="date" @change="applyFilters"
            class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
        </template>

        <!-- Action filter -->
        <div class="relative">
          <select v-model="f.action" @change="applyFilters"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">All Actions</option>
            <option value="received">Received</option>
            <option value="sent">Sent</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Source filter -->
        <div class="relative">
          <select v-model="f.source" @change="applyFilters"
            class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">All Sources</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="third_party_payment">3rd Party Payment</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Clear filters -->
        <button v-if="hasFilters" @click="clearFilters"
          class="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700
                 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 px-3 py-2.5 rounded-xl transition">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Clear
        </button>

        <!-- Spacer -->
        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="dataLoading">
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Received</p>
            <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(meta.total_received || 0) }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
            <p class="text-[10px] font-semibold text-red-400 uppercase tracking-wide">Sent</p>
            <p class="text-sm font-bold text-red-500 dark:text-red-400">Rs {{ formatNum(meta.total_sent || 0) }}</p>
          </div>
          <div class="rounded-xl px-4 py-2 text-right"
            :class="netAmount >= 0
              ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900'
              : 'bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900'">
            <p class="text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap"
              :class="netAmount >= 0 ? 'text-emerald-500' : 'text-red-400'">Net</p>
            <p class="text-sm font-bold"
              :class="netAmount >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
              Rs {{ formatNum(Math.abs(netAmount)) }}
            </p>
          </div>
          </template>
        </div>
      </div>

      <!-- ── MOBILE HEADER + STATS + FILTERS ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Indigo gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-5">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Payments</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ meta.total }} payment{{ meta.total !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats -->
          <div v-if="!dataLoading" class="grid grid-cols-3 gap-2 mb-3">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Received</p>
              <p class="text-sm font-bold text-white">Rs {{ formatNum(meta.total_received || 0) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-red-300 uppercase tracking-widest mb-1">Sent</p>
              <p class="text-sm font-bold text-white">Rs {{ formatNum(meta.total_sent || 0) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold uppercase tracking-widest mb-1"
                :class="netAmount >= 0 ? 'text-yellow-200' : 'text-orange-300'">Net</p>
              <p class="text-sm font-bold text-white">Rs {{ formatNum(Math.abs(netAmount)) }}</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-3 gap-2 mb-3">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Compact filter bar - single row -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-2.5 pb-1.5">
          <div class="flex items-center gap-2">
            <!-- Action pills -->
            <button v-for="opt in [{v:'',l:'All'},{v:'received',l:'↓ In'},{v:'sent',l:'↑ Out'}]" :key="opt.v"
              @click="f.action = opt.v; applyFilters()"
              class="shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full transition"
              :class="f.action === opt.v ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              {{ opt.l }}
            </button>

            <div class="flex-1"></div>

            <!-- Filters button with badge -->
            <button @click="mobileFiltersSheetOpen = true"
              class="relative shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition"
              :class="mobileFiltersActiveCount > 0 ? 'bg-indigo-500 text-white shadow-sm' : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="mobileFiltersActiveCount > 0"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-indigo-600 text-[10px] font-bold flex items-center justify-center shadow">
                {{ mobileFiltersActiveCount }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Flash -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 7" :key="i" class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 8" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-2.5 w-4 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse shrink-0"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-28 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-6 w-16 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
            <div class="h-3 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
            <div class="h-7 w-12 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
          </div>
        </div>
        <div class="md:hidden space-y-2.5">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3.5">
              <div class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              </div>
              <div class="space-y-1.5 text-right">
                <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
                <div class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
              </div>
            </div>
            <div class="flex items-center justify-between px-4 py-2.5 border-t border-slate-50 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
              <div class="flex gap-2">
                <div class="h-5 w-16 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse"></div>
                <div class="h-5 w-12 bg-slate-100 dark:bg-slate-700 rounded-lg animate-pulse"></div>
              </div>
              <div class="h-7 w-14 bg-slate-100 dark:bg-slate-700 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state (Desktop & Mobile) -->
      <div v-else-if="payments.length === 0">
        <!-- Desktop empty state -->
        <div class="hidden md:flex flex-col items-center justify-center py-20 text-center">
          <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75"/>
            </svg>
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium">
            {{ hasFilters ? 'No payments match your filters' : 'No payments yet' }}
          </p>
          <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
            {{ hasFilters ? 'Try adjusting your filters' : 'Record your first payment to get started' }}
          </p>
          <button v-if="hasFilters" @click="clearFilters" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear filters</button>
          <button v-else @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ Add Payment</button>
        </div>

        <!-- Mobile empty state -->
        <div class="md:hidden flex flex-col items-center justify-center py-16 text-center">
          <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75"/>
            </svg>
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium">{{ hasFilters ? 'No payments match' : 'No payments yet' }}</p>
          <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">{{ hasFilters ? 'Try adjusting your filters' : 'Record your first payment to get started' }}</p>
          <button v-if="hasFilters" @click="clearFilters" class="mt-4 text-indigo-500 text-sm font-semibold">Clear filters</button>
          <button v-else @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold">+ Add Payment</button>
        </div>
      </div>

      <template v-else>
        <!-- Desktop table -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">#</th>
                <th @click="sortBy('target_date')"
                  class="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                  :class="f.sort === 'target_date' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                  <span class="inline-flex items-center gap-1">Date <SortIcon :active="f.sort === 'target_date'" :asc="f.dir === 'asc'" /></span>
                </th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
                <th @click="sortBy('action')"
                  class="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                  :class="f.sort === 'action' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                  <span class="inline-flex items-center gap-1">Action <SortIcon :active="f.sort === 'action'" :asc="f.dir === 'asc'" /></span>
                </th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Source</th>
                <th @click="sortBy('amount')"
                  class="text-right px-5 py-3.5 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                  :class="f.sort === 'amount' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                  <span class="inline-flex items-center justify-end gap-1">Amount <SortIcon :active="f.sort === 'amount'" :asc="f.dir === 'asc'" /></span>
                </th>
                <th class="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(p, i) in payments" :key="p.id">
                <!-- Main row -->
                <tr @click="toggleExpand(p.id)"
                  class="cursor-pointer border-t border-slate-50 dark:border-slate-800 transition"
                  :class="expandedId === p.id ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'">
                  <td class="px-5 py-4 text-slate-400 text-xs">{{ (meta.current_page - 1) * meta.per_page + i + 1 }}</td>
                  <td class="px-5 py-4 text-slate-600 dark:text-slate-400 whitespace-nowrap">{{ formatDate(p.target_date) }}</td>
                  <td class="px-5 py-4">
                    <div class="flex items-center gap-2">
                      <svg class="w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200"
                        :class="expandedId === p.id ? 'rotate-90 text-indigo-500' : ''"
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                      </svg>
                      <div>
                        <p class="font-semibold text-slate-800 dark:text-white">{{ p.customer.name }}</p>
                        <p class="text-xs text-slate-400 mt-0.5">{{ p.customer.shop_name }}</p>
                      </div>
                    </div>
                  </td>
                  <td class="px-5 py-4">
                    <span :class="p.action === 'received'
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                      : 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400'"
                      class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium capitalize">
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path v-if="p.action === 'received'" stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                        <path v-else stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                      </svg>
                      {{ p.action }}
                    </span>
                  </td>
                  <td class="px-5 py-4">
                    <div class="flex items-center gap-2">
                      <span class="text-slate-600 dark:text-slate-400 text-xs">{{ sourceLabel(p.source) }}</span>
                      <span v-if="p.has_proof" @click.stop="openProof(p.proof_url)" title="View payment proof"
                        class="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 transition cursor-pointer">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                        </svg>
                      </span>
                    </div>
                  </td>
                  <td class="px-5 py-4 text-right font-semibold"
                    :class="p.action === 'received' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    Rs {{ formatNum(p.amount) }}
                  </td>
                  <td class="px-5 py-4 text-center" @click.stop>
                    <div class="relative inline-block">
                      <button @click.stop="toggleActionDropdown(p.id)"
                        class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                               hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                        </svg>
                      </button>
                      <!-- Dropdown menu -->
                      <div v-if="actionDropdownId === p.id" @click.stop
                        class="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                        <button v-if="p.can_edit" @click="openEdit(p); actionDropdownId = null"
                          class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300
                                 hover:bg-slate-50 dark:hover:bg-slate-700 transition">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                          </svg>
                          Edit
                        </button>
                        <div v-else class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 dark:text-slate-600 cursor-not-allowed">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                          </svg>
                          Edit Locked
                        </div>
                        <button @click="openDeleteConfirm(p); actionDropdownId = null"
                          class="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 dark:text-red-400
                                 hover:bg-red-50 dark:hover:bg-red-950 transition border-t border-slate-100 dark:border-slate-700">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>

                <!-- Expanded row -->
                <tr v-if="expandedId === p.id">
                  <td colspan="7" class="px-5 pb-5 bg-indigo-50/30 dark:bg-indigo-950/10 border-t border-indigo-100 dark:border-indigo-900/30">
                    <div class="mt-4 space-y-3">
                      <!-- Proof Image -->
                      <div v-if="p.has_proof" class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                        <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-2 font-semibold">Payment Proof</p>
                        <div @click="openProof(p.proof_url)"
                          class="cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:border-indigo-500 dark:hover:border-indigo-500 transition inline-block">
                          <img :src="p.proof_url" alt="Payment proof" class="max-w-xs h-auto" />
                        </div>
                      </div>

                      <!-- Notes -->
                      <div v-if="p.notes" class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                        <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1.5 font-semibold">Notes</p>
                        <p class="text-sm text-slate-700 dark:text-slate-300">{{ p.notes }}</p>
                      </div>

                      <!-- Locked message -->
                      <div v-if="!p.can_edit" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3">
                        <div class="flex items-start gap-2.5">
                          <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                          </svg>
                          <div>
                            <p class="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-0.5">Cannot Edit Payment</p>
                            <p class="text-xs text-amber-700 dark:text-amber-300">This payment cannot be edited because the customer has been deleted.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards (native style) -->
        <div class="md:hidden space-y-2.5">
          <div v-for="p in payments" :key="p.id"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">

            <!-- Card header — tappable to expand -->
            <button @click="toggleExpand(p.id)" class="w-full text-left p-4 active:bg-slate-50 dark:active:bg-slate-800/50 transition">
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <!-- Action icon -->
                  <div class="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    :class="p.action === 'received' ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-red-100 dark:bg-red-900/50'">
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"
                      :class="p.action === 'received' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                      <path v-if="p.action === 'received'" stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                    </svg>
                  </div>
                  <!-- Customer + shop -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span :class="p.action === 'received'
                        ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                        : 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'"
                        class="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md shrink-0">
                        {{ p.action }}
                      </span>
                      <span class="text-[10px] text-slate-400 font-medium">{{ sourceLabel(p.source) }}</span>
                      <svg v-if="p.has_proof" class="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" title="Has proof">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"/>
                      </svg>
                      <span class="text-[10px] text-slate-400">· {{ formatDate(p.target_date) }}</span>
                    </div>
                    <p class="font-semibold text-slate-800 dark:text-white text-sm leading-tight truncate">{{ p.customer.name }}</p>
                    <p class="text-xs text-slate-400 mt-0.5 truncate">{{ p.customer.shop_name }}</p>
                  </div>
                </div>
                <!-- Amount + chevron -->
                <div class="shrink-0 text-right">
                  <p class="text-base font-bold leading-tight"
                    :class="p.action === 'received' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    Rs {{ formatNum(p.amount) }}
                  </p>
                  <svg class="w-4 h-4 text-slate-400 mt-1.5 ml-auto transition-transform duration-200"
                    :class="expandedId === p.id ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>
              </div>
            </button>

            <!-- Expanded detail -->
            <div v-if="expandedId === p.id"
              class="border-t border-slate-100 dark:border-slate-800">

              <!-- Proof section (if exists) -->
              <div v-if="p.has_proof" class="px-4 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Payment Proof</p>
                <div @click="openProof(p.proof_url)"
                  class="cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:border-indigo-500 dark:hover:border-indigo-500 transition">
                  <img :src="p.proof_url" alt="Payment proof" class="w-full h-auto" />
                </div>
              </div>

              <!-- Notes section (if exists) -->
              <div v-if="p.notes" class="px-4 py-3 bg-slate-50/50 dark:bg-slate-800/30">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Notes</p>
                <p class="text-sm text-slate-600 dark:text-slate-300">{{ p.notes }}</p>
              </div>

              <!-- Action buttons row -->
              <div class="px-4 py-3 bg-slate-50 dark:bg-slate-800/50">
                <div class="grid grid-cols-2 gap-2">
                  <button v-if="p.can_edit" @click.stop="openEdit(p)"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400
                           bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 rounded-xl py-2.5 transition active:scale-95">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                    </svg>
                    Edit
                  </button>
                  <button v-else @click.stop="handleLockedEditClick(p)"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-slate-400 dark:text-slate-600
                           bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl py-2.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                    </svg>
                    Locked
                  </button>
                  <button @click.stop="openDeleteConfirm(p)"
                    class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400
                           bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination — Desktop -->
        <div class="hidden md:flex flex-col sm:flex-row items-center justify-between gap-3 mt-5">
          <!-- Per page -->
          <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span>Show</span>
            <div class="relative">
              <select v-model="f.per_page" @change="f.page = 1; applyFilters()"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-1.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <svg class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
            <span>of {{ meta.total }} records</span>
          </div>

          <!-- Page nav -->
          <div class="flex items-center gap-1">
            <button @click="goToPage(meta.current_page - 1)" :disabled="meta.current_page <= 1"
              class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                     text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                     disabled:opacity-40 disabled:cursor-not-allowed transition">
              ‹
            </button>
            <template v-for="pg in pageNumbers" :key="pg">
              <span v-if="pg === '...'" class="px-2 text-slate-400">…</span>
              <button v-else @click="goToPage(pg)"
                :class="pg === meta.current_page
                  ? 'bg-indigo-500 text-white border-indigo-500'
                  : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'"
                class="w-9 h-9 rounded-xl text-sm border transition">
                {{ pg }}
              </button>
            </template>
            <button @click="goToPage(meta.current_page + 1)" :disabled="meta.current_page >= meta.pages"
              class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                     text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                     disabled:opacity-40 disabled:cursor-not-allowed transition">
              ›
            </button>
          </div>
        </div>

        <!-- Load more — Mobile -->
        <div class="md:hidden mt-4 pb-6 flex flex-col items-center gap-3">
          <button v-if="meta.current_page < meta.pages" @click="loadMore" :disabled="loadingMore"
            class="flex items-center gap-2 px-6 py-2.5 rounded-2xl text-sm font-semibold border border-slate-200 dark:border-slate-700
                   bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300
                   disabled:opacity-50 active:scale-95 transition w-full justify-center">
            <svg v-if="loadingMore" class="w-4 h-4 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            <span>{{ loadingMore ? 'Loading…' : 'Load more' }}</span>
          </button>
          <p v-else-if="meta.total > 0" class="text-xs text-slate-400">All caught up ✓</p>
        </div>
      </template>

    </div>

    <!-- ── ADD / EDIT MODAL ── -->
    <Modal v-if="showModal" @close="closeModal">
      <template #title>{{ editTarget ? 'Edit Payment' : 'Add Payment' }}</template>
      <form @submit.prevent="submitForm" class="space-y-4">

        <!-- Customer dropdown with search -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Customer <span class="text-red-400">*</span></label>
          <div class="relative" ref="customerDropdownRef">
            <button type="button" @click="toggleCustomerDropdown"
              class="w-full flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white rounded-xl px-3 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 transition">
              <span :class="form.customer_id ? '' : 'text-slate-400'">
                {{ selectedCustomerLabel }}
              </span>
              <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </button>
            <div v-if="customerDropdownOpen"
              class="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     rounded-xl shadow-lg overflow-hidden">
              <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                <input v-model="customerSearch" type="text" placeholder="Search by name or shop..."
                  class="w-full bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400
                         rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  @click.stop
                  @keydown.down.prevent="moveActive(1)"
                  @keydown.up.prevent="moveActive(-1)"
                  @keydown.enter.prevent="confirmActive"
                  @keydown.escape="customerDropdownOpen = false" />
              </div>
              <div class="max-h-48 overflow-y-auto" ref="customerListRef">
                <button v-for="(c, idx) in filteredCustomers" :key="c.id" type="button"
                  :ref="el => { if (el) customerItemRefs[idx] = el }"
                  @click="selectCustomer(c)"
                  class="w-full text-left px-3 py-2.5 text-sm transition"
                  :class="idx === activeCustomerIndex
                    ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                    : form.customer_id === c.id
                      ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                  <p>{{ c.name }}</p>
                  <p class="text-xs text-slate-400">{{ c.shop_name }}</p>
                </button>
                <p v-if="filteredCustomers.length === 0" class="px-3 py-4 text-sm text-slate-400 text-center">No customers found</p>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <!-- Amount -->
          <InputField v-model="form.amount" label="Amount" placeholder="0" type="number" step="1" min="1" required />

          <!-- Target Date -->
          <InputField v-model="form.target_date" label="Date" type="date" required />

          <!-- Action -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Action <span class="text-red-400">*</span></label>
            <div class="relative">
              <select v-model="form.action" required
                class="w-full appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 pr-8 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="" disabled>Select action</option>
                <option value="received">Received</option>
                <option value="sent">Sent</option>
              </select>
              <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>

          <!-- Source -->
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Source <span class="text-red-400">*</span></label>
            <div class="relative">
              <select v-model="form.source" required
                class="w-full appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 pr-8 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="" disabled>Select source</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="third_party_payment">3rd Party Payment</option>
              </select>
              <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Notes</label>
          <textarea v-model="form.notes" rows="3" placeholder="Optional notes..."
            class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-3 py-2.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none transition"></textarea>
        </div>

        <!-- Proof Upload -->
        <ProofUpload
          v-model="proofFile"
          :existing-proof-url="proofPreviewUrl"
          input-id="payment-proof-input"
          @view-proof="openProof"
        />

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeModal"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="loading || !form.customer_id"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ loading ? 'Saving...' : (editTarget ? 'Update' : 'Add Payment') }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── Mobile filters bottom sheet ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileFiltersSheetOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileFiltersSheetOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileFiltersSheetOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 82vh"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Filters</p>
            <div class="flex items-center gap-2">
              <button v-if="hasFilters" @click="clearFilters(); mobileFiltersSheetOpen = false"
                class="text-xs font-semibold text-red-500 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/50 transition">
                Clear all
              </button>
              <button @click="mobileFiltersSheetOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Content -->
          <div class="overflow-y-auto flex-1 px-4 py-4 space-y-5">

            <!-- Customer -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Customer</p>
              <button type="button"
                @click="mobileFiltersSheetOpen = false; $nextTick(() => { mobileCustomerSheetOpen = true; filterCustomerSearch = '' })"
                class="w-full flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-2xl px-4 py-3 text-sm transition active:bg-slate-100 dark:active:bg-slate-700">
                <span :class="f.customer_id ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-500 dark:text-slate-400'">
                  {{ filterCustomerLabel }}
                </span>
                <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </button>
            </div>

            <!-- Date range -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Date Range</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in [{v:'',l:'All Time'},{v:'today',l:'Today'},{v:'yesterday',l:'Yesterday'},{v:'this_week',l:'This Week'},{v:'last_week',l:'Last Week'},{v:'this_month',l:'This Month'},{v:'last_month',l:'Last Month'},{v:'this_year',l:'This Year'}]" :key="opt.v"
                  @click="f.date_preset = opt.v; onDatePresetChange()"
                  class="text-xs font-semibold px-3.5 py-2 rounded-full transition"
                  :class="f.date_preset === opt.v ? 'bg-indigo-500 text-white shadow-sm' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
            </div>

            <!-- Source -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Source</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in [{v:'',l:'All'},{v:'cash',l:'Cash'},{v:'bank_transfer',l:'Bank Transfer'},{v:'third_party_payment',l:'3rd Party'}]" :key="opt.v"
                  @click="f.source = opt.v; applyFilters()"
                  class="text-xs font-semibold px-3.5 py-2 rounded-full transition"
                  :class="f.source === opt.v ? 'bg-indigo-500 text-white shadow-sm' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
            </div>

          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ── Mobile customer filter bottom sheet ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileCustomerSheetOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileCustomerSheetOpen = false">
        </div>
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileCustomerSheetOpen"
          class="md:hidden fixed inset-x-0 z-50 flex flex-col bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl"
          :style="{ bottom: sheetBottom + 'px', maxHeight: sheetMaxHeight }"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Title + close -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Select Customer</p>
            <button @click="mobileCustomerSheetOpen = false"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- Search -->
          <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/>
              </svg>
              <input v-model="filterCustomerSearch" type="text" placeholder="Search customers..."
                class="w-full bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white placeholder-slate-400
                       rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <!-- List -->
          <div class="overflow-y-auto flex-1">
            <!-- All customers row -->
            <button type="button" @click="selectFilterCustomer(''); mobileCustomerSheetOpen = false"
              class="w-full flex items-center gap-3 px-4 py-3.5 transition active:bg-slate-50 dark:active:bg-slate-800">
              <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                <svg class="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
                </svg>
              </div>
              <span class="flex-1 text-left text-sm font-medium"
                :class="!f.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-300'">
                All Customers
              </span>
              <svg v-if="!f.customer_id" class="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>
            <!-- Customer rows -->
            <button v-for="c in filteredFilterCustomers" :key="c.id" type="button"
              @click="selectFilterCustomer(String(c.id)); mobileCustomerSheetOpen = false"
              class="w-full flex items-center gap-3 px-4 py-3.5 border-t border-slate-50 dark:border-slate-800 transition active:bg-slate-50 dark:active:bg-slate-800">
              <div class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                :style="{ background: avatarColor(c.name) }">
                {{ c.name.charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1 text-left min-w-0">
                <p class="text-sm font-medium truncate"
                  :class="String(c.id) === f.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-800 dark:text-white'">
                  {{ c.name }}
                </p>
                <p v-if="c.shop_name" class="text-xs text-slate-400 truncate">{{ c.shop_name }}</p>
              </div>
              <svg v-if="String(c.id) === f.customer_id" class="w-5 h-5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>
            <p v-if="filteredFilterCustomers.length === 0" class="px-4 py-8 text-sm text-slate-400 text-center">No customers found</p>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Mobile Locked Alert -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4">
        <div v-if="showLockedAlert" class="md:hidden fixed inset-x-0 bottom-0 z-50 p-4">
          <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl overflow-hidden">
            <div class="flex items-start gap-3 p-4">
              <div class="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-slate-800 dark:text-white mb-1">Cannot Edit Payment</p>
                <p class="text-sm text-slate-600 dark:text-slate-300">{{ lockedAlertMessage }}</p>
              </div>
              <button @click="closeLockedAlert" class="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <button @click="closeLockedAlert" class="w-full bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-semibold py-3 border-t border-slate-200 dark:border-slate-700 active:bg-slate-100 dark:active:bg-slate-700 transition">
              Got it
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete confirmation -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Permanently Delete Payment?"
      message="This action cannot be undone. The payment will be permanently removed from your account."
      info-message="WARNING: This will permanently delete this payment and remove it from all reports and statistics. This action is irreversible."
      confirm-text="Delete Permanently"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm" />

  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import Modal from '@/components/Modal.vue'
import InputField from '@/components/InputField.vue'
import SortIcon from '@/components/SortIcon.vue'
import ProofUpload from '@/components/ProofUpload.vue'

const props = defineProps({
  customers: Array,
  filters:   Object,
})

const page   = usePage()
const errors = computed(() => page.props.errors)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

const payments    = ref([])
const meta        = ref({ total: 0, pages: 1, current_page: 1, per_page: 10, total_received: 0, total_sent: 0 })
const dataLoading = ref(true)
const loadingMore = ref(false)
const netAmount   = computed(() => (meta.value.total_received || 0) - (meta.value.total_sent || 0))

// ── Expand state ───────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Action dropdown ────────────────────────────────
const actionDropdownId = ref(null)
function toggleActionDropdown(id) {
  actionDropdownId.value = actionDropdownId.value === id ? null : id
}
function closeActionDropdown() {
  actionDropdownId.value = null
}

async function fetchData(append = false) {
  if (append) loadingMore.value = true
  else dataLoading.value = true
  try {
    const params = new URLSearchParams()
    if (f.value.customer_id) params.set('customer_id',    f.value.customer_id)
    if (f.value.date_preset) params.set('date_preset',    f.value.date_preset)
    if (f.value.date_preset === 'custom') {
      if (f.value.date_from) params.set('date_from', f.value.date_from)
      if (f.value.date_to)   params.set('date_to',   f.value.date_to)
    }
    if (f.value.action)              params.set('payment_action', f.value.action)
    if (f.value.source)              params.set('source',         f.value.source)
    if (f.value.per_page !== 10)     params.set('per_page',       f.value.per_page)
    if (f.value.page > 1)            params.set('page',           f.value.page)
    if (f.value.sort !== 'target_date') params.set('sort',        f.value.sort)
    if (f.value.dir  !== 'desc')        params.set('dir',         f.value.dir)

    const res  = await fetch(`/payments/data?${params}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    const incoming = json.payments || []
    payments.value = append ? [...payments.value, ...incoming] : incoming
    meta.value     = json.meta || meta.value

    // keep URL in sync
    const url = params.toString() ? `/payments?${params}` : '/payments'
    history.replaceState(null, '', url)
  } finally {
    dataLoading.value = false
    loadingMore.value = false
  }
}

function loadMore() {
  f.value.page++
  fetchData(true)
}

// ── Filter state (synced from server) ──────────────
const f = ref({
  customer_id: props.filters?.customer_id || '',
  date_preset: props.filters?.date_preset || '',
  date_from:   props.filters?.date_from   || '',
  date_to:     props.filters?.date_to     || '',
  action:      props.filters?.action || '',
  source:      props.filters?.source      || '',
  per_page:    props.filters?.per_page    || 10,
  page:        props.filters?.page        || 1,
  sort:        props.filters?.sort        || 'target_date',
  dir:         props.filters?.dir         || 'desc',
})

const perPageOptions = [10, 20, 50, 100, 250, 500, 1000]

const hasFilters = computed(() =>
  f.value.customer_id || f.value.date_preset || f.value.action || f.value.source
)

function applyFilters() {
  f.value.page = 1
  fetchData()
}

function sortBy(col) {
  if (f.value.sort === col) {
    f.value.dir = f.value.dir === 'desc' ? 'asc' : 'desc'
  } else {
    f.value.sort = col
    f.value.dir  = 'desc'
  }
  f.value.page = 1
  applyFilters()
}

function onDatePresetChange() {
  if (f.value.date_preset !== 'custom') {
    f.value.date_from = ''
    f.value.date_to   = ''
    applyFilters()
  }
}

function clearFilters() {
  f.value = { customer_id: '', date_preset: '', date_from: '', date_to: '', action: '', source: '', per_page: f.value.per_page, page: 1 }
  applyFilters()
}

// ── Pagination ──────────────────────────────────────
function goToPage(pg) {
  if (pg < 1 || pg > meta.value.pages) return
  f.value.page = pg
  applyFilters()
}

const pageNumbers = computed(() => {
  const total   = meta.value.pages
  const current = meta.value.current_page
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = []
  if (current <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', total)
  } else if (current >= total - 3) {
    pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total)
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total)
  }
  return pages
})

// ── Helpers ─────────────────────────────────────────
function formatNum(n) {
  return Number(n).toLocaleString('en-PK')
}

function formatDate(d) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`
}

function sourceLabel(s) {
  return { cash: 'Cash', bank_transfer: 'Bank Transfer', third_party_payment: '3rd Party' }[s] || s
}

// ── Customer dropdown with search ───────────────────
const customerDropdownOpen = ref(false)
const customerSearch       = ref('')
const customerDropdownRef  = ref(null)
const customerListRef      = ref(null)
const customerItemRefs     = ref([])
const activeCustomerIndex  = ref(-1)

const filteredCustomers = computed(() => {
  const q = customerSearch.value.trim().toLowerCase()
  // Only show active (non-deleted) customers in the modal
  const activeCustomers = props.customers.filter(c => !c.deleted_at)
  if (!q) return activeCustomers
  return activeCustomers.filter(c =>
    c.name.toLowerCase().includes(q) || c.shop_name.toLowerCase().includes(q)
  )
})

const selectedCustomerLabel = computed(() => {
  if (!form.value.customer_id) return 'Select customer'
  const c = props.customers.find(c => c.id === form.value.customer_id)
  return c ? c.name : 'Select customer'
})

function toggleCustomerDropdown() {
  customerDropdownOpen.value = !customerDropdownOpen.value
  if (customerDropdownOpen.value) {
    customerSearch.value      = ''
    activeCustomerIndex.value = -1
    customerItemRefs.value    = []
  }
}

function selectCustomer(c) {
  form.value.customer_id     = c.id
  customerDropdownOpen.value = false
  activeCustomerIndex.value  = -1
}

function moveActive(dir) {
  const len = filteredCustomers.value.length
  if (len === 0) return
  activeCustomerIndex.value = (activeCustomerIndex.value + dir + len) % len
  const el = customerItemRefs.value[activeCustomerIndex.value]
  el?.scrollIntoView({ block: 'nearest' })
}

function confirmActive() {
  if (activeCustomerIndex.value >= 0 && filteredCustomers.value[activeCustomerIndex.value]) {
    selectCustomer(filteredCustomers.value[activeCustomerIndex.value])
  }
}

watch(customerSearch, () => {
  activeCustomerIndex.value = -1
  customerItemRefs.value    = []
})

// ── Filter customer dropdown with search ─────────────
const filterCustomerDropdownOpen = ref(false)
const filterCustomerSearch       = ref('')
const filterCustomerDropdownRef  = ref(null)
const filterCustomerListRef      = ref(null)
const filterCustomerItemRefs     = ref([])
const activeFilterCustomerIndex  = ref(-1)

// ── Mobile filters bottom sheet ──────────────────────
const mobileFiltersSheetOpen  = ref(false)
const mobileFiltersActiveCount = computed(() =>
  (f.value.customer_id ? 1 : 0) + (f.value.date_preset ? 1 : 0) + (f.value.source ? 1 : 0)
)

// ── Mobile customer filter bottom sheet ──────────────
const mobileCustomerSheetOpen = ref(false)
const sheetBottom             = ref(0)
const sheetMaxHeight          = ref('82vh')

const AVATAR_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444','#14b8a6','#f97316','#84cc16']
function avatarColor(name) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function onViewportResize() {
  if (!window.visualViewport) return
  const vv        = window.visualViewport
  const keyboardH = window.innerHeight - vv.height - vv.offsetTop
  sheetBottom.value    = Math.max(0, keyboardH)
  sheetMaxHeight.value = `${vv.height * 0.82}px`
}

watch(mobileCustomerSheetOpen, (open) => {
  if (open) {
    window.visualViewport?.addEventListener('resize', onViewportResize)
    onViewportResize()
  } else {
    window.visualViewport?.removeEventListener('resize', onViewportResize)
    sheetBottom.value    = 0
    sheetMaxHeight.value = '82vh'
  }
})

const filteredFilterCustomers = computed(() => {
  const q = filterCustomerSearch.value.trim().toLowerCase()
  if (!q) return props.customers
  return props.customers.filter(c =>
    c.name.toLowerCase().includes(q) || c.shop_name.toLowerCase().includes(q)
  )
})

const filterCustomerLabel = computed(() => {
  if (!f.value.customer_id) return 'All Customers'
  const c = props.customers.find(c => String(c.id) === f.value.customer_id)
  return c ? c.name : 'All Customers'
})

function toggleFilterCustomerDropdown() {
  filterCustomerDropdownOpen.value = !filterCustomerDropdownOpen.value
  if (filterCustomerDropdownOpen.value) {
    filterCustomerSearch.value      = ''
    activeFilterCustomerIndex.value = -1
    filterCustomerItemRefs.value    = []
  }
}

function selectFilterCustomer(id) {
  f.value.customer_id              = id
  filterCustomerDropdownOpen.value = false
  activeFilterCustomerIndex.value  = -1
  applyFilters()
}

function moveFilterActive(dir) {
  const len = filteredFilterCustomers.value.length
  if (len === 0) return
  activeFilterCustomerIndex.value = (activeFilterCustomerIndex.value + dir + len) % len
  filterCustomerItemRefs.value[activeFilterCustomerIndex.value]?.scrollIntoView({ block: 'nearest' })
}

function confirmFilterActive() {
  if (activeFilterCustomerIndex.value >= 0 && filteredFilterCustomers.value[activeFilterCustomerIndex.value]) {
    selectFilterCustomer(String(filteredFilterCustomers.value[activeFilterCustomerIndex.value].id))
  }
}

watch(filterCustomerSearch, () => {
  activeFilterCustomerIndex.value = -1
  filterCustomerItemRefs.value    = []
})

function onClickOutside(e) {
  if (customerDropdownRef.value && !customerDropdownRef.value.contains(e.target)) {
    customerDropdownOpen.value = false
  }
  if (filterCustomerDropdownRef.value && !filterCustomerDropdownRef.value.contains(e.target)) {
    filterCustomerDropdownOpen.value = false
  }
}

onMounted(() => {
  document.title = `Payments | ${businessName.value}`
  document.addEventListener('click', onClickOutside)
  document.addEventListener('click', closeActionDropdown)
  fetchData()
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('click', closeActionDropdown)
  window.visualViewport?.removeEventListener('resize', onViewportResize)
})

// ── Modal state ──────────────────────────────────────
const showModal  = ref(false)
const editTarget = ref(null)
const loading    = ref(false)

const today = new Date().toISOString().split('T')[0]
const emptyForm = () => ({ customer_id: null, amount: '', action: '', source: '', target_date: today, notes: '' })
const form = ref(emptyForm())
const proofFile = ref(null)
const proofPreviewUrl = ref(null)

function openAdd() {
  editTarget.value = null
  form.value = emptyForm()
  proofFile.value = null
  proofPreviewUrl.value = null
  showModal.value = true
}

function openEdit(p) {
  editTarget.value = p
  form.value = {
    customer_id: p.customer_id,
    amount:      p.amount,
    action:      p.action,
    source:      p.source,
    target_date: p.target_date,
    notes:       p.notes || '',
  }
  proofFile.value = null
  proofPreviewUrl.value = p.proof_url || null
  showModal.value = true
}

function closeModal() {
  showModal.value  = false
  editTarget.value = null
  customerDropdownOpen.value = false
  proofFile.value = null
  proofPreviewUrl.value = null
}

// ── Handle locked edit click (mobile alert) ──────────
const showLockedAlert = ref(false)
const lockedAlertMessage = ref('')

function handleLockedEditClick(payment) {
  // Only show alert on mobile
  if (window.innerWidth < 768) {
    lockedAlertMessage.value = 'Cannot edit this payment because the customer has been deleted.'
    showLockedAlert.value = true
  }
}

function closeLockedAlert() {
  showLockedAlert.value = false
  lockedAlertMessage.value = ''
}

// ── Delete payment ─────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

function openDeleteConfirm(payment) {
  deleteTarget.value = payment
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteTarget.value = null
}

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/payments/${deleteTarget.value.id}`, {
      onSuccess: () => {
        closeDeleteConfirm()
        fetchData()
        resolve()
      },
      onError: () => {
        resolve()
      }
    })
  })
}

function submitForm() {
  loading.value = true
  const payload = { ...form.value }
  if (proofFile.value) {
    payload.proof = proofFile.value
  }
  const req  = useForm({ payment: payload })
  const opts = {
    onSuccess: () => { closeModal(); fetchData() },
    onFinish:  () => { loading.value = false },
  }

  if (editTarget.value) {
    req.patch(`/payments/${editTarget.value.id}`, opts)
  } else {
    req.post('/payments', opts)
  }
}

// Open proof in new window/tab
function openProof(url) {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}
</script>
